import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Award } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import PhotoUpload from "@/components/PhotoUpload";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const EMPTY = { name: "", logo_url: "", link: "" };

export default function SponsorsTab() {
    const [sponsors, setSponsors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = () =>
        api.get("/sponsors")
            .then((r) => setSponsors(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setOpen(true);
    };
    const openEdit = (s) => {
        setEditing(s);
        setForm({ name: s.name, logo_url: s.logo_url || "", link: s.link || "" });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editing) await api.put(`/sponsors/${editing.id}`, form);
            else await api.post("/sponsors", form);
            toast.success(editing ? "Sponsor updated" : "Sponsor added to the homepage marquee");
            setOpen(false);
            load();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (s) => {
        if (!window.confirm(`Remove ${s.name} from the sponsors marquee?`)) return;
        try {
            await api.delete(`/sponsors/${s.id}`);
            toast.success("Sponsor removed");
            load();
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    return (
        <div data-testid="admin-sponsors-tab">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h2 className="font-display font-bold uppercase text-xl text-white">Sponsors ({sponsors.length})</h2>
                <button
                    data-testid="add-sponsor-button"
                    onClick={openCreate}
                    className="flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                >
                    <Plus className="w-4 h-4" /> Add Sponsor
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" /> Loading sponsors…
                </div>
            ) : sponsors.length === 0 ? (
                <div className="card-arena rounded-3xl p-12 text-center" data-testid="admin-sponsors-empty">
                    <Award className="w-10 h-10 text-[#FF5500] mx-auto mb-3 opacity-60" />
                    <p className="font-display font-bold uppercase text-white">No sponsors yet</p>
                    <p className="text-sm text-slate-400 mt-1.5">
                        Add your first sponsor — their logo scrolls across the homepage marquee.
                    </p>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sponsors.map((s) => (
                        <div
                            key={s.id}
                            data-testid={`admin-sponsor-row-${s.id}`}
                            className="card-arena rounded-2xl p-4 flex items-center gap-3"
                        >
                            {s.logo_url ? (
                                <img
                                    src={s.logo_url}
                                    alt={s.name}
                                    className="w-12 h-12 rounded-xl object-contain bg-[#1A1A28] border border-[#262638] shrink-0 p-1"
                                />
                            ) : (
                                <div className="w-12 h-12 rounded-xl bg-[#1A1A28] border border-[#262638] flex items-center justify-center shrink-0">
                                    <Award className="w-5 h-5 text-slate-500" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="font-display font-bold uppercase text-white truncate">{s.name}</p>
                                <p className="text-[10px] text-slate-500 truncate">{s.link || "No link"}</p>
                            </div>
                            <button
                                data-testid={`edit-sponsor-${s.id}`}
                                onClick={() => openEdit(s)}
                                className="p-2 text-slate-400 hover:text-[#FFB800] transition-colors duration-200"
                                aria-label={`Edit ${s.name}`}
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button
                                data-testid={`delete-sponsor-${s.id}`}
                                onClick={() => remove(s)}
                                className="p-2 text-slate-400 hover:text-[#EF4444] transition-colors duration-200"
                                aria-label={`Remove ${s.name}`}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-[#12121A] border-[#262638] text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black uppercase text-2xl">
                            {editing ? "Edit Sponsor" : "Add Sponsor"}
                        </DialogTitle>
                        <DialogDescription className="sr-only">Sponsor details form</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} data-testid="sponsor-form" className="space-y-4 mt-2">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Sponsor Name</Label>
                            <Input
                                data-testid="sponsor-form-name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="e.g. Northern Sports Bar"
                                className="mt-2 bg-[#08080C] border-[#262638] text-white"
                                required
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">
                                Website / Link (optional)
                            </Label>
                            <Input
                                data-testid="sponsor-form-link"
                                value={form.link}
                                onChange={(e) => setForm({ ...form, link: e.target.value })}
                                placeholder="https://…"
                                className="mt-2 bg-[#08080C] border-[#262638] text-white"
                            />
                        </div>
                        <PhotoUpload
                            label="Sponsor Logo"
                            testid="sponsor-form-logo"
                            value={form.logo_url}
                            onChange={(url) => setForm({ ...form, logo_url: url })}
                        />
                        <button
                            type="submit"
                            data-testid="sponsor-form-submit"
                            disabled={saving}
                            className="w-full flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-50 text-white font-display font-bold uppercase tracking-widest text-sm py-3 rounded-full transition-colors duration-200"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            {editing ? "Save Changes" : "Add Sponsor"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
